import {
  AnnotationType,
  canUseTool,
  MeasurementToolType,
  SelectedImageTools,
  SimpsonMeasurementType,
} from '@/state/slices/measurementSession';
import { SPECTRAL_DOPPLER_TOOL_NAMES } from '@/features/study-detail/Viewer/tools/SpectralDoppler';
import type { MeasurementTool } from '@/models/report';

/**
 * Aliases (case-insensitive, whitespace-trimmed) that resolve to a Simpson
 * measurement type. Exact match — substring match would falsely trigger on
 * indexed/derived variants like "LVEDVi" containing "LVEDV".
 */
const SIMPSON_ALIASES: Record<string, SimpsonMeasurementType> = {
  // EDV — Left Ventricular end-Diastolic Volume
  lvdv: 'EDV',
  lvedv: 'EDV',
  'lv edv': 'EDV',
  // ESV — Left Ventricular end-Systolic Volume
  lvsv: 'ESV',
  lvesv: 'ESV',
  'lv esv': 'ESV',
  // LAV — Left Atrial Volume
  'la volume': 'LAV',
  lav: 'LAV',
  'la vol': 'LAV',
};

export const SIMPSON_MEASUREMENTS = Object.keys(SIMPSON_ALIASES);

/**
 * List of units that are manual-entry only (for reference).
 * Note: 'ml' is kept here because not all ml measurements use Simpson -
 * only the specific ones in SIMPSON_MEASUREMENTS do.
 */
export const MANUAL_ENTRY_UNITS = ['%', 'm/s', 'ms', 'ml', 'cm3', 'mm3'];

/**
 * List of measurement labels that are manual-entry only (calculated or Doppler values).
 * Simpson measurements are removed from this list as they are now clickable.
 * Spectral Doppler labels (velocities, gradients, VTI) are handled separately
 * via SPECTRAL_DOPPLER_MEASUREMENTS below so they become tool-clickable when a
 * spectral region is available.
 */
export const MANUAL_ENTRY_MEASUREMENTS = ['LV EF', 'E/A ratio', 'E/e′ ratio'];

/** Which derived value on a SpectralDopplerResult fills this field. */
type SpectralFieldRole =
  | 'peakV'
  | 'meanV'
  | 'vti'
  | 'durationMs'
  | 'deltaT'
  | 'slope'
  | 'peakGradient'
  | 'meanGradient';

interface SpectralFieldMapping {
  variant: 'probe' | 'segment' | 'vti';
  role: SpectralFieldRole;
  /** Unit the value should be converted to for report display. */
  unit: 'm/s' | 'cm' | 'mmHg' | 'ms' | 'cm/s2';
}

/**
 * Labels captured by the spectral Doppler tools, with the derived-value role
 * that should flow into each field. Clickability is gated at call-site on
 * availability (useSpectralDopplerAvailability).
 *
 * Generic labels at the top serve as fallbacks for unqualified rows. The
 * valve/site-qualified labels below cover the structured echo template
 * (LVOT / AoV / MV / TV / TR / PV / AR / MR / RV / pulmonary vein).
 *
 * Lookups normalize via `normalizeSpectralLabel` (lowercase + ASCII
 * apostrophe), so keys are stored with the typography we expect from the
 * server template. If the live template differs, add the exact form here.
 */
export const SPECTRAL_DOPPLER_MEASUREMENTS: Record<
  string,
  SpectralFieldMapping
> = {
  // Generic fallbacks
  'Peak velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  Vmax: { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'E-wave velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'A-wave velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  "e' velocity": { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'Peak gradient': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'Mean gradient': { variant: 'segment', role: 'meanGradient', unit: 'mmHg' },
  'Deceleration time': { variant: 'segment', role: 'deltaT', unit: 'ms' },
  'Deceleration slope': { variant: 'segment', role: 'slope', unit: 'cm/s2' },
  VTI: { variant: 'vti', role: 'vti', unit: 'cm' },
  'Mean velocity': { variant: 'vti', role: 'meanV', unit: 'm/s' },

  // Mitral inflow / annulus (PW Doppler & Tissue Doppler)
  'MV E': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'MV A': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'E wave': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'A wave': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  "Lateral e'": { variant: 'probe', role: 'peakV', unit: 'm/s' },
  "Septal e'": { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'MV DcT': { variant: 'segment', role: 'deltaT', unit: 'ms' },
  'MV deceleration time': { variant: 'segment', role: 'deltaT', unit: 'ms' },
  'MV mean PG': { variant: 'segment', role: 'meanGradient', unit: 'mmHg' },
  'meanPG MV': { variant: 'segment', role: 'meanGradient', unit: 'mmHg' },
  'MV PHT': { variant: 'segment', role: 'deltaT', unit: 'ms' },
  'Pressure half time': { variant: 'segment', role: 'deltaT', unit: 'ms' },
  IVRT: { variant: 'segment', role: 'deltaT', unit: 'ms' },

  // Pulmonary vein Doppler
  'Peak S-wave': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'Peak D-wave': { variant: 'probe', role: 'peakV', unit: 'm/s' },

  // LVOT / Aortic valve
  'LVOT VTI': { variant: 'vti', role: 'vti', unit: 'cm' },
  'LVOT peak velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'LVOT max flow velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'LVOT peak gradient': {
    variant: 'probe',
    role: 'peakGradient',
    unit: 'mmHg',
  },
  'AoV peak velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'AoV peak vel': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'AoV peak PG': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'AoV mean PG': { variant: 'segment', role: 'meanGradient', unit: 'mmHg' },
  'AoV VTI': { variant: 'vti', role: 'vti', unit: 'cm' },
  'AV VTI': { variant: 'vti', role: 'vti', unit: 'cm' },

  // Aortic regurgitation
  'AR peak velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'AR pressure half time': { variant: 'segment', role: 'deltaT', unit: 'ms' },
  'VTI AR': { variant: 'vti', role: 'vti', unit: 'cm' },
  'AR VTI': { variant: 'vti', role: 'vti', unit: 'cm' },

  // Mitral regurgitation
  'MR peak velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'MR peak PG': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'MR VTI': { variant: 'vti', role: 'vti', unit: 'cm' },

  // Tricuspid valve & TR
  'TV peak E-wave velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'TV peak A-wave velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'TV peak E-wave': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'TV peak A-wave': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  "Lateral annulus e' velocity": {
    variant: 'probe',
    role: 'peakV',
    unit: 'm/s',
  },
  'TV inflow VTI': { variant: 'vti', role: 'vti', unit: 'cm' },
  'TV VTI': { variant: 'vti', role: 'vti', unit: 'cm' },
  'TV peak PG': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'TV mean PG': { variant: 'segment', role: 'meanGradient', unit: 'mmHg' },
  'TR peak velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'TR peak PG': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'TRmax Vel': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'TRmax PG': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'Vmax TR': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'TR VTI': { variant: 'vti', role: 'vti', unit: 'cm' },

  // Pulmonary valve & PR
  'PV peak velocity': { variant: 'probe', role: 'peakV', unit: 'm/s' },
  'PV peak gradient': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'PV peak PG': { variant: 'probe', role: 'peakGradient', unit: 'mmHg' },
  'PR pressure half time': { variant: 'segment', role: 'deltaT', unit: 'ms' },
  'PA acceleration time': { variant: 'segment', role: 'deltaT', unit: 'ms' },

  // RV systolic
  "RV S'": { variant: 'probe', role: 'peakV', unit: 'm/s' },
};

/**
 * Normalize a label for spectral lookup: lowercase, trim, and fold the Unicode
 * prime (′ U+2032) to ASCII apostrophe so `e′` and `e'` are treated alike.
 */
function normalizeSpectralLabel(label: string): string {
  return label.toLowerCase().trim().replace(/′/g, "'");
}

const SPECTRAL_LOOKUP: Record<string, SpectralFieldMapping> =
  Object.fromEntries(
    Object.entries(SPECTRAL_DOPPLER_MEASUREMENTS).map(([k, v]) => [
      normalizeSpectralLabel(k),
      v,
    ])
  );

export function isSpectralDopplerMeasurement(label: string): boolean {
  return normalizeSpectralLabel(label) in SPECTRAL_LOOKUP;
}

export function getSpectralDopplerFieldMapping(
  label: string
): SpectralFieldMapping | null {
  return SPECTRAL_LOOKUP[normalizeSpectralLabel(label)] ?? null;
}

export function getSpectralDopplerVariant(
  label: string
): 'probe' | 'segment' | 'vti' | null {
  return getSpectralDopplerFieldMapping(label)?.variant ?? null;
}

/**
 * Extract the specific value (in the field's preferred unit) from a
 * SpectralDopplerResult's value bundle. Returns null when the requested role
 * isn't populated by the result's tool variant (e.g. asking for VTI from a probe).
 */
export function extractSpectralValueForField(
  mapping: SpectralFieldMapping,
  values: {
    peakV_cmS?: number;
    meanV_cmS?: number;
    vti_cm?: number;
    durationMs?: number;
    deltaT_sec?: number;
    slope_cmS2?: number;
    peakGradient_mmHg?: number;
    meanGradient_mmHg?: number;
  }
): number | null {
  switch (mapping.role) {
    case 'peakV':
      // Peak velocity reports as a magnitude — the derived value is signed
      // (a peak below the baseline is negative), but the worksheet field is
      // positive, matching the AI convention and the header/on-image display.
      return values.peakV_cmS != null ? Math.abs(values.peakV_cmS) / 100 : null;
    case 'meanV':
      return values.meanV_cmS != null ? values.meanV_cmS / 100 : null;
    case 'vti':
      return values.vti_cm ?? null;
    case 'durationMs':
      return values.durationMs ?? null;
    case 'deltaT':
      return values.deltaT_sec != null ? values.deltaT_sec * 1000 : null;
    case 'slope':
      return values.slope_cmS2 ?? null;
    case 'peakGradient':
      return values.peakGradient_mmHg ?? null;
    case 'meanGradient':
      return values.meanGradient_mmHg ?? null;
    default:
      return null;
  }
}

/**
 * Check if a measurement label requires Simpson's Method for volume calculation.
 * These measurements trace the endocardial border to compute chamber volumes (EDV, ESV, LAV).
 */
export function isSimpsonMeasurement(label: string): boolean {
  return label.toLowerCase().trim() in SIMPSON_ALIASES;
}

/**
 * Get the Simpson measurement type from a measurement label.
 *
 * Simpson's Method ("Method of Disks") computes cardiac chamber volumes by
 * tracing the endocardial border and summing the volumes of stacked disks.
 * Returns:
 * - EDV (LV end-diastolic volume)
 * - ESV (LV end-systolic volume)
 * - LAV (left atrial volume)
 * EF = (EDV - ESV) / EDV × 100%; normal 55–70%, <40% indicates HFrEF.
 */
export function getSimpsonMeasurementType(
  label: string
): SimpsonMeasurementType | null {
  return SIMPSON_ALIASES[label.toLowerCase().trim()] ?? null;
}

/**
 * Normalize a unit string for comparison:
 * lowercase, trim, and replace Unicode superscript ² (U+00B2) with ASCII '2'.
 *
 * This ensures 'cm²' and 'cm2' are treated identically across the codebase.
 */
export function normalizeUnit(unit: string): string {
  return unit
    .toLowerCase()
    .trim()
    .replace(/²|\u00B2/g, '2');
}

/**
 * Binding table: backend abstract tool id -> internal MeasurementToolType.
 *
 * The backend has already gated tool attachment per row (see `MeasurementTool`
 * on the report model). These ids map onto the internal tool types, so
 * the rest of the pipeline (activation via `getCornerstoneToolName`, capture)
 * is unchanged. There is intentionally no `angle` id — angle rows arrive with
 * `tool: null` and stay manual-entry.
 */
export const BACKEND_TOOL_TO_TOOL_TYPE: Record<
  MeasurementTool,
  MeasurementToolType
> = {
  LINEAR: 'length',
  AREA: 'area',
  VOLUMETRIC: 'simpson',
  VELOCITY_PROBE: 'spectral-doppler-probe',
  VELOCITY_SEGMENT: 'spectral-doppler-segment',
  VTI_TRACE: 'spectral-doppler-vti',
  SLOPE: 'spectral-doppler-segment',
  // Time rows are in ms, so they draw with the Time tool.
  PW_DOPPLER_TIME_INTERVAL: 'spectral-doppler-segment',
};

/**
 * Resolve a backend-supplied tool id to an internal tool type.
 *
 * - null/undefined -> no drawing tool (manual input or formula-computed row).
 * - known id       -> the matching MeasurementToolType.
 * - unknown non-null id -> log and attach no tool (defensive; the backend
 *   should never send one, but we must never crash).
 */
/** Row tool wins when the row exists (null = backend gated off); the label's
 *  tool covers rows that don't exist yet (pass rowTool as undefined then). */
export function resolveMeasurementTool(
  rowTool: MeasurementTool | null | undefined,
  labelTool: MeasurementTool | null | undefined
): MeasurementTool | null {
  return rowTool !== undefined ? rowTool : (labelTool ?? null);
}

export function getToolTypeFromBackendTool(
  tool: string | null | undefined
): MeasurementToolType | null {
  if (!tool) return null;
  // Keys are the UPPERCASE ids the backend serves verbatim — direct lookup, no
  // normalization. Use hasOwnProperty so prototype keys ("toString", …) can't
  // resolve to an inherited Object.prototype member and slip past the guard.
  const toolType = Object.hasOwn(BACKEND_TOOL_TO_TOOL_TYPE, tool)
    ? BACKEND_TOOL_TO_TOOL_TYPE[tool as MeasurementTool]
    : undefined;
  if (!toolType) {
    // eslint-disable-next-line no-console -- defensive: surface backend tool ids the FE binding doesn't know about
    console.warn(
      `[measurementToolMapping] Unknown backend tool id "${tool}" — attaching no tool.`
    );
    return null;
  }
  return toolType;
}

/** The viewer's Simpson tool: traced with PlanarFreehandROI, measured as a volume. */
export const SIMPSON_TOOL_NAME = 'Simpson';

/**
 * Maps tool type to the viewer's tool name (a Cornerstone tool, or Simpson).
 */
export function getCornerstoneToolName(toolType: MeasurementToolType): string {
  switch (toolType) {
    case 'length':
      return 'Length';
    case 'area':
      return 'PlanarFreehandROI';
    case 'simpson':
      return SIMPSON_TOOL_NAME;
    case 'angle':
      return 'Angle';
    case 'spectral-doppler-probe':
      return SPECTRAL_DOPPLER_TOOL_NAMES.PROBE;
    case 'spectral-doppler-segment':
      return SPECTRAL_DOPPLER_TOOL_NAMES.SEGMENT;
    case 'spectral-doppler-vti':
      return SPECTRAL_DOPPLER_TOOL_NAMES.VTI;
    default:
      return 'Length';
  }
}

/** Persisted annotation type per measurement tool type — exhaustive by
 *  construction, so a new tool type won't compile without a row. */
const TOOL_ANNOTATION_TYPE: Record<MeasurementToolType, AnnotationType> = {
  length: 'LINE',
  angle: 'ANGLE',
  area: 'FREEHAND',
  simpson: 'FREEHAND',
  'spectral-doppler-probe': 'POINT',
  'spectral-doppler-segment': 'LINE',
  'spectral-doppler-vti': 'LINE',
};

const TOOL_TYPE_BY_VIEWER_TOOL = new Map(
  (Object.keys(TOOL_ANNOTATION_TYPE) as MeasurementToolType[]).map((type) => [
    getCornerstoneToolName(type),
    type,
  ])
);

/** canUseTool for a viewer tool name; Pan, Zoom and W/L are never gated. */
export function canUseViewerTool(
  toolName: string,
  image: SelectedImageTools | null | undefined
): boolean {
  const type = TOOL_TYPE_BY_VIEWER_TOOL.get(toolName);
  return !type || canUseTool(type, image);
}

/** Backend annotation type for an active measurement's tool type. */
export function getAnnotationTypeForToolType(
  toolType: MeasurementToolType | undefined
): AnnotationType {
  return toolType ? TOOL_ANNOTATION_TYPE[toolType] : 'LINE';
}
